import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiAward, FiCheckCircle, FiFileText, FiUsers, FiTrendingUp } = FiIcons;

const TrustAndTransparency = () => {
  const certifications = [
    {
      icon: FiShield,
      title: "Section 8 NGO",
      description: "Registered Non-Profit",
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: FiAward,
      title: "12A Certified",
      description: "Income Tax Exemption",
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: FiCheckCircle,
      title: "80G Certified",
      description: "Tax Deduction Benefits",
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    }
  ];

  const transparencyFeatures = [
    {
      icon: FiFileText,
      title: "100% Fund Utilization",
      description: "Every rupee goes directly to rescue, rehabilitation, and skills training programs",
      stat: "100%"
    },
    {
      icon: FiUsers,
      title: "Regular Updates",
      description: "Donors receive monthly progress reports with photos and impact stories",
      stat: "Monthly"
    },
    {
      icon: FiTrendingUp,
      title: "Impact Reports",
      description: "Detailed annual reports showing measurable outcomes and transformations",
      stat: "Annual"
    }
  ];

  const fundsFlow = [
    { step: "Rescue", percentage: 25, description: "Identifying and reaching people in need" },
    { step: "Shelter", percentage: 30, description: "Safe accommodation and basic necessities" },
    { step: "Skills", percentage: 35, description: "Training programs and education" },
    { step: "Jobs", percentage: 10, description: "Placement and ongoing support" }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiShield} className="w-4 h-4" />
            <span>Trust & Transparency</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Trust is Our
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              Foundation
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We believe in complete transparency. See exactly how your donations create impact and transform lives.
          </p>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
        >
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.title}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl p-8 text-center shadow-lg border-2 border-gray-100 hover:shadow-xl transition-all duration-300"
            >
              <div className={`w-16 h-16 ${cert.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                <SafeIcon icon={cert.icon} className={`w-8 h-8 ${cert.color}`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{cert.title}</h3>
              <p className="text-gray-600">{cert.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Transparency Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left - Features */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Complete Transparency</h3>
            <div className="space-y-8">
              {transparencyFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-4"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={feature.icon} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-xl font-semibold text-gray-900">{feature.title}</h4>
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {feature.stat}
                      </span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Funds Flow */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Funds Flow: Rescue → Shelter → Skills → Jobs
            </h3>
            
            <div className="space-y-6">
              {fundsFlow.map((flow, index) => (
                <motion.div
                  key={flow.step}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="space-y-3"
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">{flow.step}</span>
                    <span className="text-2xl font-bold text-blue-600">{flow.percentage}%</span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${flow.percentage}%` }}
                      transition={{ duration: 1.5, delay: index * 0.2 }}
                      viewport={{ once: true }}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-600">{flow.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              className="mt-8 p-4 bg-green-100 rounded-lg text-center"
            >
              <div className="text-sm font-medium text-green-800 mb-1">Total Impact</div>
              <div className="text-2xl font-bold text-green-700">100% Direct Benefit</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="relative">
              <h3 className="text-3xl font-bold mb-4">Donate with Complete Confidence</h3>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Your donation is 100% tax-deductible and you'll receive regular updates on the lives you're transforming.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors duration-200">
                  🛡️ Donate Securely Now
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200">
                  📄 Download Annual Report
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustAndTransparency;